import * as THREE from 'three';
import { MTLLoader } from 'three-stdlib';
import { OBJLoader } from 'three-stdlib';

// 设备像素比
let globalPixel = window.devicePixelRatio;

// 简单的深拷贝函数
function clone_obj(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
}

// 合并多个图片到一个画布上
async function mergeImages(imageUrls: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
        if (imageUrls.length === 0) {
            resolve('');
            return;
        }

        if (imageUrls.length === 1) {
            resolve(imageUrls[0]);
            return;
        }

        // 创建画布
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject(new Error('无法创建画布上下文'));
            return;
        }

        // 设置画布尺寸
        canvas.width = 500;
        canvas.height = 500;

        let loadedCount = 0;
        const images: HTMLImageElement[] = [];

        // 加载所有图片
        imageUrls.forEach((url, index) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                images[index] = img;
                loadedCount++;

                // 所有图片加载完成后开始合并
                if (loadedCount === imageUrls.length) {
                    // 清空画布
                    ctx.clearRect(0, 0, canvas.width, canvas.height);

                    // 依次绘制每张图片
                    images.forEach(img => {
                        if (img) {
                            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        }
                    });

                    // 导出合并后的图片
                    const mergedDataUrl = canvas.toDataURL('image/png');
                    resolve(mergedDataUrl);
                }
            };
            img.onerror = () => {
                reject(new Error(`图片加载失败: ${url}`));
            };
            img.src = url;
        });
    });
}

// 编辑时渲染3d面
function initFaceEdit(
    wval: number,
    hval: number,
    position: { x: number; y: number; z: number },
    rotation: { x: number; y: number; z: number },
    scale: { x: number; y: number; z: number },
    index: number,
    base64: string,
    threeParmas: { scene: THREE.Scene; renderer: THREE.WebGLRenderer; camera: THREE.Camera }
): Promise<string> {
    // console.log(rotation);
    wval = wval || 200;
    hval = hval || 200;
    return new Promise((resolve, reject) => {
        let cube = new THREE.PlaneGeometry(wval, hval);
        let texture = new THREE.TextureLoader().load(base64, () => {
            // 关键设置：修正颜色空间
            texture.colorSpace = THREE.SRGBColorSpace; // Three.js r152+ 使用这个
            // 如果是旧版本，使用：texture.encoding = THREE.sRGBEncoding;

            let material = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.DoubleSide,
                transparent: true,
                alphaTest: 0.1,
                color: 0xffffff, // 确保基础颜色是白色
                opacity: 1.0     // 确保完全不透明
            });

            let mesh = new THREE.Mesh(cube, material);
            mesh.position.set(position.x, position.y, position.z);
            mesh.rotation.set(rotation.x, rotation.y, rotation.z);
            mesh.scale.set(scale.x, scale.y, scale.z);
            threeParmas.scene.add(mesh);
            threeParmas.renderer.render(threeParmas.scene, threeParmas.camera);
            resolve("1");
        });
    });
}


// 初始化3d画布dom
async function initThreeDom(): Promise<{ scene: THREE.Scene; renderer: THREE.WebGLRenderer; camera: THREE.Camera }> {
    return new Promise((resolve, reject) => {
        // 创建静态画布合成3d图片和mask
        // let canvasYs = document.createElement('canvas');
        //抗锯齿
        // canvasYs.style.width = '500px';
        // canvasYs.style.height = '500px';
        // canvasYs.width = 2500;
        // canvasYs.height = 2500;
        // canvasThree.appendChild(canvasYs);
        // 创建场景
        let scene = new THREE.Scene();
        // 不设置背景，保持透明
        // 创建渲染器
        let renderer = new THREE.WebGLRenderer({
            // canvas: canvasYs,
            preserveDrawingBuffer: true,
            antialias: true,
            precision: "highp",
            alpha: true, // 设置为true支持透明背景
        });
        // renderer.setSize(2500, 2500);
        let linPixel = globalPixel < 1.5 ? 1.5 : globalPixel;
        renderer.setSize(500, 500);
        renderer.setPixelRatio(linPixel);
        const threeContent = document.getElementById("three-content");
        if (threeContent) {
            threeContent.appendChild(renderer.domElement);
        }
        // 初始相机
        let camera = new THREE.PerspectiveCamera(45, 500 / 500, 1, 10000);
        camera.position.set(0, 169.999999999915, 0.00017000755638153979);
        camera.rotation.x = -1.5707953267504475;
        scene.add(camera);
        // 完全移除光照系统，MeshBasicMaterial不需要光照
        let back = {
            scene: scene,
            renderer: renderer,
            camera: camera,
        };
        resolve(back);
    });
}

// 编辑时渲染3d模型
function loadObjModelEdit(
    mtlName: string,
    objName: string,
    position: { x: number; y: number; z: number },
    rotation: { x: number; y: number; z: number },
    scale: { x: number; y: number; z: number },
    index: number,
    base64: string,
    threeParmas: { scene: THREE.Scene; renderer: THREE.WebGLRenderer; camera: THREE.Camera }
): Promise<string> {
    return new Promise((resolve, reject) => {
        //初始化MTL材质加载器
        let mtlLoader = new MTLLoader();
        //初始化obj模型加载器
        let objLoader = new OBJLoader();

        mtlLoader.load(mtlName, (materials: any) => {
            // obj的模型会和MaterialCreator包含的材质对应起来
            objLoader.setMaterials(materials);
            objLoader.load(objName, (obj: any) => {
                let bb = obj.children[0];
                bb.position.set(position.x, position.y, position.z);
                bb.rotation.set(rotation.x, rotation.y, rotation.z);
                bb.scale.set(scale.x, scale.y, scale.z);

                // 创建新的基础材质，确保贴图可见且支持透明
                let tex = new THREE.TextureLoader().load(base64, () => {
                    // 修正：在回调中使用已经加载的纹理
                    let newMaterial = new THREE.MeshBasicMaterial({
                        map: tex,  // 现在tex已经可用
                        side: THREE.DoubleSide,
                        transparent: true,
                        alphaTest: 0.1,
                        color: 0xffffff, // 确保基础颜色正确
                        opacity: 1.0     // 确保完全不透明
                    });
                    bb.material = newMaterial;
                
                    //把模型添加到场景
                    threeParmas.scene.add(bb);
                    threeParmas.renderer.render(
                        threeParmas.scene,
                        threeParmas.camera
                    );
                    resolve("1");
                });
                
                // 设置颜色空间（防止发白）
                tex.colorSpace = THREE.SRGBColorSpace;
            });
        });
    });
}

// 导出3d图片
export async function exportImages(o: any, layout: any): Promise<any[]> {
    try {
        let backThree = await initThreeDom();
        let handleImageArr: string[] = [];
        let tempImageAll: any[] = [];
        // 收集所有需要渲染的面
        const facesToRender: any[] = [];
        for (let i in layout.faces) {
            // 是否匹配得到code
            let backArr: any[] = [];
            // o.screenshot 是一个对象，需要遍历其值来查找匹配的 code
            for (const key in o.screenshot) {
                if (o.screenshot[key].code === layout.faces[i].code) {
                    backArr = [o.screenshot[key]];
                    break;
                }
            }
            if (backArr.length > 0) {
                if (backArr[0].timestamp && backArr[0].timestamp < Date.now() - 60 * 1000) {
                    return [];
                }
                facesToRender.push({
                    face: layout.faces[i],
                    image: backArr[0].image,
                    index: parseInt(i)
                });
            }
        }
        console.log(facesToRender)
        var image2d: string[] = []; 
        // 创建所有渲染任务的 Promise 数组
        const renderPromises = facesToRender.map(async (faceData) => {
            const { face, image, index } = faceData;
            if (face.obj) {
                // 有3d建模数据
                return loadObjModelEdit(
                    "/images/common.mtl",
                    face.obj,
                    face.position,
                    face.rotation,
                    face.scale,
                    index,
                    image,
                    backThree
                );
            } else if (face.rotation) {
                return initFaceEdit(
                    face.width,
                    face.height,
                    face.position,
                    face.rotation,
                    face.scale,
                    index,
                    image,
                    backThree
                );
            } else {
                image2d.push(image);
                return Promise.resolve(); // 2D 图片不需要异步处理
            }
        });

        console.log(image2d)
        // 等待所有渲染完成
        await new Promise(resolve => setTimeout(resolve, 200));

        // 最终渲染并导出合并后的图片
        backThree.renderer.render(backThree.scene, backThree.camera);
        let imgData = backThree.renderer.domElement.toDataURL("image/png");

        // 将image2d的图片数组和imgData合并 
        let finalImageData = imgData;
        if (image2d.length > 0) {
            finalImageData = await mergeImages([imgData, ...image2d]);
        }

        handleImageArr.push(finalImageData);
        //+ "?x-oss-process=style/medium"
        if (layout.backgrounds && layout.backgrounds.length > 0) {
            for (let k in layout.backgrounds) {
                let newImageArr: string[] = [];
                newImageArr.push(
                    layout.backgrounds[k].src
                );
                newImageArr = newImageArr.concat(handleImageArr);
                if (layout.covers[k]) {
                    let coverSrc = layout.covers[k].src.replace("images/");
                    newImageArr.push(
                        coverSrc
                    );
                } 
                tempImageAll.push({
                    optionsValueId: layout.backgrounds[k].option_value_id,
                    optionName: layout.backgrounds[k].optionName,
                    image: clone_obj(newImageArr),
                });
            }
        } else if (layout.covers && layout.covers.length > 0) {
            for (let k in layout.covers) {
                let newImageArr: string[] = [];
                newImageArr = newImageArr.concat(handleImageArr);
                if (layout.covers[k]) {
                    let coverSrc = layout.covers[k].src.replace("images/");
                    newImageArr.push(
                        o.urlPath + coverSrc
                    );
                }
                tempImageAll.push({
                    optionsValueId: layout.backgrounds[k].option_value_id,
                    optionName: layout.backgrounds[k].optionName,
                    image: clone_obj(newImageArr),
                });
            }
        } 
        return tempImageAll;
    } catch (error) {
        console.error("exportImages 执行出错:", error);
        return [];
    }
}



